// src/services/businessService.js
import { supabase } from '../lib/supabase';
import { apiFetch } from './apiClient';

export const businessService = {
  // ============================================================================
  // BUSINESS PROFILE
  // ============================================================================

  /**
   * Get business profile
   */
  async getBusinessProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('business_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching business profile:', error);
      return { data: null, error };
    }
  },

  /**
   * Create or update business profile
   */
  async updateBusinessProfile(userId, profileData) {
    try {
      const { data, error } = await supabase
        .from('business_profiles')
        .upsert({
          user_id: userId,
          ...profileData,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'  // Specify which field to use for conflict detection
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating business profile:', error);
      return { data: null, error };
    }
  },

  // ============================================================================
  // CUSTOMER CONNECTIONS
  // ============================================================================

  /**
   * Get network connections for the current user via GET /v1/network/connections.
   * Counterparty is resolved server-side so this works for both B2C and B2B
   * (see PR #168 read-side fix, PR #169 principal embed adds phone/has_calendar).
   *
   * Pagination: requests up to 500 rows in one fetch. If the API returns a
   * non-null next_cursor we surface `hasMore: true` so the UI can warn the
   * operator that the list was truncated. Real cursor pagination is a follow-up.
   */
  async getNetworkConnections() {
    try {
      const body = await apiFetch('/v1/network/connections?status=active&limit=500');
      const hasMore = !!body?.pagination?.next_cursor;
      if (hasMore) {
        console.warn('[networkConnections] response was truncated at 500 rows; pagination not yet implemented');
      }
      return { data: body.connections || [], hasMore, error: null };
    } catch (error) {
      console.error('Error fetching network connections:', error);
      return { data: null, hasMore: false, error };
    }
  },

  /**
   * Get customer connection count
   */
  async getCustomerCount(businessId) {
    try {
      // OR pattern matches the same B2B fix as PRs #168 / #170 / #171.
      // "customer count" name is now misleading post-B2B — counts active
      // network counterparties regardless of which side the user sits on.
      const { count, error } = await supabase
        .from('business_customer_connections')
        .select('*', { count: 'exact', head: true })
        .or(`business_id.eq.${businessId},customer_id.eq.${businessId}`)
        .eq('status', 'active');

      if (error) throw error;
      return { data: count, error: null };
    } catch (error) {
      console.error('Error fetching customer count:', error);
      return { data: 0, error };
    }
  },

  // ============================================================================
  // APPOINTMENT OFFERS — REMOVED in Wave 4
  // ============================================================================
  // The legacy offer-sending and offer-listing methods, plus the legacy
  // getUpcomingAppointments (which read the offer table), have all been
  // deleted. Use proposalService against /v1/proposals instead.


  // ============================================================================
  // AVAILABILITY MATCHING
  // ============================================================================

  /**
   * Find availability matches between business and opted-in customers
   * This is the core matching algorithm
   */
  async findAvailabilityMatches(businessId, filters = {}) {
    try {
      const {
        daysAhead = 14,
        minDuration = 30, // minutes
        startDate = null,
        endDate = null
      } = filters;

      // Define time window
      const start = startDate || new Date();
      const end = endDate || new Date(Date.now() + daysAhead * 24 * 60 * 60 * 1000);

      // Get business profile to know appointment duration
      const { data: businessProfile } = await this.getBusinessProfile(businessId);
      const appointmentDuration = businessProfile?.default_appointment_duration || minDuration;

      // Get all active network counterparties (B2B + B2C). Mirrors the OR
      // pattern from PR #168 (networkService) and PR #170 (NetworkTab) so a
      // future grep for "ownerOrFilter pattern" surfaces all three at once.
      // The downstream variable names ("customers", "customerIds") are kept
      // as-is to bound the diff — they're misleading post-B2B (these may be
      // role=business counterparties) but functional. Real fix: build a
      // server-side /v1/availability/match primitive and migrate (follow-up #31).
      const { data: rows, error: customersError } = await supabase
        .from('business_customer_connections')
        .select('business_id, customer_id')
        .or(`business_id.eq.${businessId},customer_id.eq.${businessId}`)
        .eq('status', 'active');

      if (customersError) throw customersError;

      if (!rows || rows.length === 0) {
        return { data: [], error: null };
      }

      // "customers" here is misleading post-B2B — these are network counterparties, may be role=business
      const customerIds = rows.map(r =>
        r.business_id === businessId ? r.customer_id : r.business_id
      );

      // Wave 4: legacy "filter out already-sent offers" step removed along
      // with the legacy offer table. Proposal-aware dedup will live in a
      // follow-up that queries /v1/proposals; for now matches may surface
      // slots that already have a proposal in flight.
      const existingOffers = [];

      // Get business's busy blocks
      const { data: businessBusy, error: businessError } = await supabase
        .from('availability_blocks')
        .select('start_time, end_time')
        .eq('user_id', businessId)
        .eq('is_available', false)
        .gte('start_time', start.toISOString())
        .lte('start_time', end.toISOString())
        .order('start_time');

      if (businessError) throw businessError;

      // Get all customers' busy blocks
      const { data: customersBusy, error: customersError2 } = await supabase
        .from('availability_blocks')
        .select('user_id, start_time, end_time')
        .in('user_id', customerIds)
        .eq('is_available', false)
        .gte('start_time', start.toISOString())
        .lte('start_time', end.toISOString())
        .order('start_time');

      if (customersError2) throw customersError2;

      // Get customer preferences
      const { data: customerPrefs, error: prefsError } = await supabase
        .from('customer_preferences')
        .select('*')
        .in('user_id', customerIds);

      if (prefsError) throw prefsError;

      // Get customer details
      // Get the user's session token for RLS
      const { data: { session } } = await supabase.auth.getSession();
      const userToken = session?.access_token;

      // Fetch profiles with user's JWT token (required for RLS to work properly)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const profilesResponse = await fetch(
        `${supabaseUrl}/rest/v1/profiles?select=id,email,full_name&id=in.(${customerIds.join(',')})`,
        {
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${userToken || supabaseKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const customerDetails = await profilesResponse.json();

      // Find matches using a simple algorithm
      const matches = this._findMatchingSlots({
        businessBusy: businessBusy || [],
        customersBusy: customersBusy || [],
        customerPrefs: customerPrefs || [],
        customerDetails: customerDetails || [],
        existingOffers: existingOffers || [],
        startDate: start,
        endDate: end,
        appointmentDuration
      });

      return { data: matches, error: null };
    } catch (error) {
      console.error('Error finding availability matches:', error);
      return { data: null, error };
    }
  },

  /**
   * Internal helper to find matching free slots
   */
  _findMatchingSlots(params) {
    const {
      businessBusy,
      customersBusy,
      customerPrefs,
      customerDetails,
      existingOffers,
      startDate,
      endDate,
      appointmentDuration
    } = params;

    const matches = [];
    const durationMs = appointmentDuration * 60 * 1000;

    // Helper to check if an offer already exists for this customer at this time
    const hasExistingOffer = (customerId, slotStart, slotEnd) => {
      return existingOffers.some(offer => {
        if (offer.customer_id !== customerId) return false;
        const offerStart = new Date(offer.proposed_start_time).getTime();
        const offerEnd = new Date(offer.proposed_end_time).getTime();
        const slotStartMs = slotStart.getTime();
        const slotEndMs = slotEnd.getTime();
        // Check for any overlap
        return (slotStartMs < offerEnd && slotEndMs > offerStart);
      });
    };

    // Simple algorithm: For each customer, find time slots where both are free
    customerDetails.forEach(customer => {
      const customerId = customer.id;

      // Get this customer's busy blocks
      const customerBusyBlocks = customersBusy.filter(b => b.user_id === customerId);

      // Get customer preferences
      const prefs = customerPrefs.find(p => p.user_id === customerId);

      // Generate potential time slots (every hour from 9am to 5pm for simplicity)
      const slots = this._generateTimeSlots(startDate, endDate, prefs);

      // Check each slot
      slots.forEach(slot => {
        const slotStart = new Date(slot.start);
        const slotEnd = new Date(slotStart.getTime() + durationMs);

        // Check if business is free
        const businessIsFree = !this._isTimeBlocked(slotStart, slotEnd, businessBusy);

        // Check if customer is free
        const customerIsFree = !this._isTimeBlocked(slotStart, slotEnd, customerBusyBlocks);

        // Check if offer already sent for this slot
        const offerAlreadySent = hasExistingOffer(customerId, slotStart, slotEnd);

        if (businessIsFree && customerIsFree && !offerAlreadySent) {
          matches.push({
            customer_id: customerId,
            customer_name: customer.full_name || customer.email,
            customer_email: customer.email,
            start_time: slotStart.toISOString(),
            end_time: slotEnd.toISOString(),
            duration: appointmentDuration
          });
        }
      });
    });

    return matches;
  },

  /**
   * Generate potential time slots based on preferences
   */
  _generateTimeSlots(startDate, endDate, preferences) {
    const slots = [];
    const current = new Date(startDate);
    current.setHours(9, 0, 0, 0); // Start at 9am

    while (current <= endDate) {
      // Skip weekends by default (can be enhanced with preferences)
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        // Generate hourly slots from 9am to 5pm
        for (let hour = 9; hour < 17; hour++) {
          const slot = new Date(current);
          slot.setHours(hour, 0, 0, 0);

          slots.push({ start: slot.toISOString() });
        }
      }

      // Move to next day
      current.setDate(current.getDate() + 1);
      current.setHours(9, 0, 0, 0);
    }

    return slots;
  },

  /**
   * Check if a time slot overlaps with any busy blocks
   */
  _isTimeBlocked(slotStart, slotEnd, busyBlocks) {
    return busyBlocks.some(block => {
      const blockStart = new Date(block.start_time);
      const blockEnd = new Date(block.end_time);

      // Check for overlap
      return slotStart < blockEnd && slotEnd > blockStart;
    });
  },

  // ============================================================================
  // STATISTICS
  // ============================================================================

  /**
   * Get business dashboard statistics
   */
  async getDashboardStats(businessId, proposals = []) {
    // Wave 4: stats derive from the proposals list passed in by the dashboard
    // (via proposalService.list). Only the customer-count query still hits
    // Supabase directly.
    try {
      // OR pattern — see PR #168 / #170 / #171 for the B2B fix rationale.
      const customersResult = await supabase
        .from('business_customer_connections')
        .select('id', { count: 'exact', head: true })
        .or(`business_id.eq.${businessId},customer_id.eq.${businessId}`)
        .eq('status', 'active');

      const now = Date.now();
      const pending = proposals.filter(p => p.status === 'proposed').length;
      const accepted = proposals.filter(p => p.status === 'accepted');
      const upcoming = accepted.filter(p => {
        const slot = (p.slots || []).find(s => s.id === p.selected_slot_id) || (p.slots || [])[0];
        if (!slot?.start_time) return false;
        return new Date(slot.start_time).getTime() >= now;
      }).length;

      return {
        data: {
          optedInCustomers: customersResult.count || 0,
          pendingOffers: pending,
          upcomingAppointments: upcoming,
          totalAcceptedOffers: accepted.length
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return { data: null, error };
    }
  }
};
